import React from 'react';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { Tooltip, TooltipContent, TooltipTrigger } from '../../components/ui/tooltip';
import { Palmtree, Stethoscope, Calendar, Heart, ChevronRight } from 'lucide-react';
import { formatMinutes, formatMinutesDecimal } from './utils';
import { StatusBadge } from './StatusCards';
import { formatLastFirst } from '../../lib/nameFormat';

const formatVacationDays = (n) => {
  if (n == null || n === 0) return '0';
  if (Number.isInteger(n)) return `${n}`;
  return n.toFixed(1).replace('.', ',');
};

export const EmployeeSummaryTable = ({ employeeSummary, onRowClick }) => (
  <div className="rounded-lg border">
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>Mitarbeiter</TableHead>
          <TableHead>Heute</TableHead>
          <TableHead className="text-right">Wochensoll</TableHead>
          <TableHead className="text-right">Tage</TableHead>
          <TableHead className="text-right">Abwesenheit</TableHead>
          <TableHead className="text-right">Brutto</TableHead>
          <TableHead className="text-right">Pausen</TableHead>
          <TableHead className="text-right">Netto</TableHead>
          <TableHead className="text-right">Soll (Zeitraum)</TableHead>
          <TableHead className="text-right">Differenz</TableHead>
          <TableHead className="w-8" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {employeeSummary.length === 0 ? (
          <TableRow>
            <TableCell colSpan={11} className="text-center text-muted-foreground py-8">
              Keine Daten für diesen Zeitraum
            </TableCell>
          </TableRow>
        ) : (
          employeeSummary.map((emp) => {
            const hasAbsence = (emp.vacationDays || 0) > 0
              || (emp.sickDays || 0) > 0
              || (emp.absenceOtherDays || 0) > 0
              || (emp.holidaysInPeriod || 0) > 0;
            return (
              <TableRow
                key={emp.id}
                className={onRowClick ? 'cursor-pointer hover:bg-stone-50' : ''}
                onClick={onRowClick ? () => onRowClick(emp) : undefined}
                data-testid={`emp-row-${emp.id}`}
              >
                <TableCell>
                  <div>
                    <p className="font-medium">{formatLastFirst(emp)}</p>
                    <p className="text-xs text-muted-foreground">{emp.employee_number}</p>
                  </div>
                </TableCell>
                <TableCell><StatusBadge status={emp.todayStatus} /></TableCell>
                <TableCell className="text-right font-mono text-muted-foreground" data-testid={`emp-weekly-${emp.id}`}>
                  {(emp.weekly_hours || 40).toFixed(2).replace('.', ',')} h
                </TableCell>
                <TableCell className="text-right font-mono">{emp.workDays}</TableCell>
                <TableCell className="text-right">
                  {hasAbsence ? (
                    <div className="flex flex-wrap justify-end gap-1">
                      {(emp.vacationDays || 0) > 0 && (
                        <Badge variant="outline" className="text-sky-700 border-sky-300 bg-sky-50">
                          <Palmtree className="h-3 w-3 mr-1" />
                          {formatVacationDays(emp.vacationDays)}
                        </Badge>
                      )}
                      {(emp.sickDays || 0) > 0 && (
                        <Badge variant="outline" className="text-orange-700 border-orange-300 bg-orange-50">
                          <Stethoscope className="h-3 w-3 mr-1" />
                          {emp.sickDays}
                        </Badge>
                      )}
                      {(emp.absenceOtherDays || 0) > 0 && (
                        <Badge variant="outline" className="text-purple-700 border-purple-300 bg-purple-50">
                          <Heart className="h-3 w-3 mr-1" />
                          {emp.absenceOtherDays}
                        </Badge>
                      )}
                      {(emp.holidaysInPeriod || 0) > 0 && (
                        <Badge variant="outline" className="text-red-700 border-red-300 bg-red-50">
                          <Calendar className="h-3 w-3 mr-1" />
                          {emp.holidaysInPeriod}
                        </Badge>
                      )}
                    </div>
                  ) : <span className="text-muted-foreground">-</span>}
                </TableCell>
                <TableCell className="text-right font-mono">
                  <Tooltip><TooltipTrigger className="cursor-default">{formatMinutes(emp.totalWork)}</TooltipTrigger>
                    <TooltipContent>{formatMinutesDecimal(emp.totalWork)} Std</TooltipContent></Tooltip>
                </TableCell>
                <TableCell className="text-right font-mono">
                  <Tooltip><TooltipTrigger className="cursor-default">{formatMinutes(emp.totalBreak)}</TooltipTrigger>
                    <TooltipContent>{formatMinutesDecimal(emp.totalBreak)} Std</TooltipContent></Tooltip>
                </TableCell>
                <TableCell className="text-right font-mono font-medium">
                  <Tooltip><TooltipTrigger className="cursor-default">{formatMinutes(emp.totalNet)}</TooltipTrigger>
                    <TooltipContent>{formatMinutesDecimal(emp.totalNet)} Std</TooltipContent></Tooltip>
                </TableCell>
                <TableCell className="text-right font-mono text-muted-foreground">
                  <Tooltip><TooltipTrigger className="cursor-default">{formatMinutes(Math.round(emp.expectedMinutes))}</TooltipTrigger>
                    <TooltipContent>{formatMinutesDecimal(Math.round(emp.expectedMinutes))} Std</TooltipContent></Tooltip>
                </TableCell>
                <TableCell className={`text-right font-mono font-medium ${emp.difference >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  <Tooltip><TooltipTrigger className="cursor-default">
                    {emp.difference >= 0 ? '+' : ''}{formatMinutes(Math.round(emp.difference))}
                  </TooltipTrigger>
                    <TooltipContent>{emp.difference >= 0 ? '+' : ''}{formatMinutesDecimal(Math.round(emp.difference))} Std</TooltipContent></Tooltip>
                </TableCell>
                <TableCell className="text-right">
                  {onRowClick && <ChevronRight className="h-4 w-4 text-muted-foreground inline" />}
                </TableCell>
              </TableRow>
            );
          })
        )}
      </TableBody>
    </Table>
  </div>
);
